/**
 * Every spin on the board that the player is allowed to cut short — GDD §13.
 *
 * ── What a "spin" is here, and what it is not ──
 *
 * ONLY the part of a sequence that is running down a clock: a strip turning
 * for `spinDurationMs` before it is told where to stop. Nothing else is
 * registered, so nothing else can be skipped. Landings, lids, doors, build-ups
 * and settles all play in full, because they are the reveal — the waiting is
 * not.
 *
 * ── Why a registry and not a flag ──
 *
 * A Golden Mystery board can have thirty cells spinning at once, each with its
 * own place in the stop cascade, and the player must be able to bring them
 * down ONE AT A TIME by tapping — not collapse the whole board with a single
 * press. That needs an ordered list of what is currently waiting, which is
 * exactly what this is.
 *
 * Each press takes the LOWEST stop order still waiting. That is the cell the
 * cascade was going to stop next anyway, so spamming does not reorder the
 * feature or change which cell lands where — it only compresses the gaps. The
 * player sees the same board arrive in the same order, faster.
 *
 * ── Groups: the case where one at a time is wrong ──
 *
 * The mystery build-up is not a cascade. Every covered cell plays the same clip
 * at the same moment, and they open together — so cutting them one press at a
 * time would leave the board half-tense, which is worse than either state. An
 * entry may therefore name a GROUP, and a press that takes one entry takes
 * every entry sharing its group.
 *
 * Ungrouped entries are untouched by this: they are still taken one per press,
 * exactly as the coins always were.
 *
 * Module-level state is deliberate: there is one board, and the alternative is
 * threading a ref through the reel set, the overlay reel, the runner and every
 * cell controller for something that has exactly one instance.
 */

/** id -> { order, cancel } for every spin currently running its clock. */
const waiting = new Map();
const listeners = new Set();
let nextId = 1;

const notify = () => {
  for (const listener of listeners) listener();
};

/**
 * Declare a spin skippable for as long as it is waiting.
 *
 * @param   {number}   order  Place in the stop cascade. Lower goes first, which
 *   must match the order the sequence would have stopped them in by itself.
 * @param   {Function} cancel Ends this spin's wait early. Called at most once.
 * @param   {object}   [options]
 * @param   {string}   [options.group] Cancel together. A press that takes any
 *   entry in a group takes all of them. Omit for one-per-press.
 * @param   {boolean}  [options.pointerOnly] Refuse the spacebar for this spin.
 *   For a screen where the SPACEBAR ALSO STARTS the thing being skipped — the
 *   gamble conveyor. Without it, one deliberate press to gamble and a second
 *   reflexive one would cut the spin the instant it began, and a player who
 *   held the key would never see the wheel turn at all. A click is unambiguous
 *   there because nothing else on that screen is listening for one.
 * @returns {number}   Handle for release().
 */
export const registerSkippableSpin = (order, cancel, options = {}) => {
  const id = nextId++;
  waiting.set(id, {
    order,
    cancel,
    group: options.group ?? null,
    pointerOnly: options.pointerOnly === true,
  });
  notify();
  return id;
};

/**
 * Stop declaring it. MUST be called however the wait ends — skipped, run out,
 * or destroyed — or a dead cancel stays on the list and swallows a press.
 */
export const releaseSkippableSpin = (id) => {
  if (id == null || !waiting.has(id)) return;
  waiting.delete(id);
  notify();
};

/**
 * Cut short the next spin the cascade was going to stop — and, if that spin
 * belongs to a group, everything else in it.
 *
 * @param   {"pointer"|"key"} [source] What the player did. A spin registered
 *   `pointerOnly` is invisible to "key", so the spacebar passes over it and
 *   takes the next eligible one instead — or does nothing.
 * @returns {boolean} false when nothing eligible was waiting, which lets the
 *   caller leave the press alone rather than swallowing it.
 */
export const skipNextSpin = (source = "pointer") => {
  if (waiting.size === 0) return false;

  let bestId = null;
  let bestOrder = Infinity;
  for (const [id, entry] of waiting) {
    if (source === "key" && entry.pointerOnly) continue;
    if (entry.order < bestOrder) {
      bestOrder = entry.order;
      bestId = id;
    }
  }
  if (bestId === null) return false;

  // A grouped winner takes its whole group; an ungrouped one takes only itself.
  // The group is collected WITHOUT the source filter on purpose: the group is
  // one performance, so a spacebar press that was allowed to take any member
  // takes the rest of it too rather than leaving part of it running.
  const { group } = waiting.get(bestId);
  const taking =
    group === null
      ? [bestId]
      : [...waiting].filter(([, e]) => e.group === group).map(([id]) => id);

  // Off the list BEFORE cancelling: a cancel resolves a promise whose own
  // cleanup calls release, and re-entering here mid-iteration is how this kind
  // of registry usually goes wrong.
  const cancels = taking.map((id) => waiting.get(id).cancel);
  for (const id of taking) waiting.delete(id);
  notify();

  for (const cancel of cancels) {
    try {
      cancel();
    } catch {
      // A controller torn down between registering and now. Already gone.
    }
  }
  return true;
};

/** How many spins are waiting. The subscribe/getSnapshot pair for React. */
export const skippableSpinCount = () => waiting.size;

export const subscribeToSkippableSpins = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
